import { HTTP_STATUS_CODES, MESSAGES } from "@/config/constants";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { fetchRenewalDetails } from "./quoteAPI";
import { setErrorData } from "../error/errorSlice";

const buildPackagesCoversSnapshot = (packages = {}, covers = {}) => {
  const pkgTokens = Object.keys(packages)
    .filter((key) => packages[key]?.isOpted)
    .map((key) => `PKG:${key}`);

  const coverTokens = Object.entries(covers).flatMap(([coverCode, cover]) => {
    if (!cover) return [];

    if (Array.isArray(cover.value)) {
      return cover.value
        .filter((opt) => opt?.opted)
        .map((opt) => `COVER:${coverCode}:${String(opt?.name || "").trim()}`);
    }

    return cover?.opted ? [`COVER:${coverCode}`] : [];
  });

  return [...pkgTokens, ...coverTokens].sort();
};

const initialMigrationDetails = {
  migrationProductCode: "",
  migrationTenure: "",
  migrationSI: "",
  migrationSIOptions: [],
  migrationPremium: {},
  migrationRoomOptions: [],
  migrationRoomCategory: "",
  migrationSchemeName: "",
  migrationFromProductCode: "",
  migrationToProductCode: "",
  isMigrationPolicy: false,
  isMigrationPolicySelected: false,
};

const initialState = {
  renewalDetails: {},
  upSellPlan: {
    isUpSell: false,
    sumInsured: 0,
    premium: "",
  },
  proposalPayload: {
    key: "",
    value: "",
  },
  status: "idle",
  selectedCard: "currentPlan",
  migrationDetails: initialMigrationDetails,
  optedCovers: {},
  preComputedPackagesCovers: {
    customPlan: [],
    migrationPlan: [],
  },
  packagesCoversDirty: {
    customPlan: false,
    migrationPlan: false,
  },
  showProposalPremiumModal: false,
  utmMedium: "",
  productCode: "",
  mobileNumber: "",
};

export const fetchQuotes = createAsyncThunk(
  "quote/fetchQuotes",
  async (id, thunkApi) => {
    try {
      const response = await fetchRenewalDetails(id);
      if (response.statusCode === HTTP_STATUS_CODES.OK) {
        return response.data;
      } else {
        //TODO: dispatch an error, request did not produce expected response.
        thunkApi.dispatch(
          setErrorData({
            show: true,
            msg: response.message,
            status: response.statusCode,
            // Redirecting to URL after closing Error Modal
            url: "/",
          })
        );
        return []; //dummyData.data;
      }
    } catch (err) {
      console.error("Something went wrong", err);
      thunkApi.dispatch(
        setErrorData({
          show: true,
          msg: err.data.message || MESSAGES.DEFAULT_MESSAGE,
          status: "",
          // Redirecting to URL after closing Error Modal
          url: "/",
        })
      );
    }
  }
);

export const quoteSlice = createSlice({
  name: "quotes",
  initialState,
  reducers: {
    setRenewalDetails: (state, action) => {
      state.renewalDetails = action.payload;
    },

    clearRenewalDetails: (state) => {
      state.renewalDetails = initialState.renewalDetails;
    },

    setUpSell: (state, action) => {
      state.upSellPlan = action.payload;
    },

    setBoltQuoteId: (state, action) => {
      state.boltQuoteId = action.payload;
    },

    updateProposalPayload: (state, action) => {
      state.proposalPayload = {
        ...state.proposalPayload,
        ...action.payload,
      };
    },

    setPropSelectedCard: (state, action) => {
      state.selectedCard = action.payload;
    },

    setMigrationDetails: (state, action) => {
      state.migrationDetails = {
        ...state.migrationDetails,
        ...action.payload,
      };
    },

    setOptedCovers: (state, action) => {
      state.optedCovers = action.payload;
    },

    savePreComputedPackagesCovers: (state, action) => {
      const { cardType, packages = {}, covers = {} } = action.payload;

      state.preComputedPackagesCovers[cardType] = buildPackagesCoversSnapshot(
        packages,
        covers
      );
      state.packagesCoversDirty[cardType] = false;
      state.showProposalPremiumModal = false;
      state.selectedCard = "currentPlan";
    },

    checkPackagesCoversDirty: (state, action) => {
      const { cardType, packages = {}, covers = {} } = action.payload;

      const currentSnapshot = buildPackagesCoversSnapshot(packages, covers);
      const previousSnapshot = state.preComputedPackagesCovers[cardType] || [];

      const isDirty =
        JSON.stringify(previousSnapshot) !== JSON.stringify(currentSnapshot);

      state.packagesCoversDirty[cardType] = isDirty;

      if (isDirty) {
        state.showProposalPremiumModal = true;
      }
    },

    hideProposalPremiumModal: (state) => {
      state.showProposalPremiumModal = false;
    },

    resetPackagesCoversState: (state) => {
      state.preComputedPackagesCovers = {
        customPlan: [],
        migrationPlan: [],
      };
      state.packagesCoversDirty = {
        customPlan: false,
        migrationPlan: false,
      };
      state.showProposalPremiumModal = false;
    },

    setUtmMedium: (state, action) => {
      state.utmMedium = action.payload;
    },
    setProductCode: (state, action) => {
      state.productCode = action.payload;
    },
    setMobileNumber: (state, action) => {
      state.mobileNumber = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchQuotes.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchQuotes.fulfilled, (state, action) => {
        state.status = "idle";
        state.renewalDetails = action.payload;
        state.preComputedPackagesCovers = {
          customPlan: [],
          migrationPlan: [],
        };
        state.packagesCoversDirty = {
          customPlan: false,
          migrationPlan: false,
        };
        state.showProposalPremiumModal = false;
      });
  },
});

export const {
  setRenewalDetails,
  clearRenewalDetails,
  setUpSell,
  setBoltQuoteId,
  updateProposalPayload,
  setPropSelectedCard,
  setMigrationDetails,
  setOptedCovers,
  savePreComputedPackagesCovers,
  checkPackagesCoversDirty,
  hideProposalPremiumModal,
  resetPackagesCoversState,
  setUtmMedium,
  setProductCode,
  setMobileNumber,
} = quoteSlice.actions;

export const getRenewalDetailsDataSelector = (state) =>
  state.quotes?.renewalDetails;
export const getMasterDetails = (state) => state.proposal?.masterDetails;
export const getUpSellDataSelector = (state) => state.quotes?.upSellPlan;
export const getBoltQuoteId = (state) => state.quotes?.boltQuoteId;
export const getSelectedCard = (state) => state.quotes?.selectedCard;
export const getMigrationDetails = (state) => state.quotes?.migrationDetails;
export const getOptedCovers = (state) => state.quotes?.optedCovers;
export const getCalculatedCustomPlan = (state) =>
  state.quotes?.calculatedCustomPlan;

export const getPreComputedPackagesCovers = (state) =>
  state.quotes?.preComputedPackagesCovers;
export const getPackagesCoversDirty = (state) =>
  state.quotes?.packagesCoversDirty;
export const getCustomPlanDirty = (state) =>
  state.quotes?.packagesCoversDirty?.customPlan;
export const getMigrationPlanDirty = (state) =>
  state.quotes?.packagesCoversDirty?.migrationPlan;
export const getShowProposalPremiumModal = (state) =>
  state.quotes?.showProposalPremiumModal;
export const getIsApp = (state) => state.quotes?.utmMedium;
export const getProductCode = (state) => state.quotes?.productCode;
export const getMobileNumber = (state) => state.quotes?.mobileNumber;

export default quoteSlice.reducer;
